// Group/CreateGroupModal.js
import React, { useState } from 'react'
import { Modal, Input, Button, Checkbox, Upload, message } from 'antd'
import { IoClose, IoImage, IoPersonAdd, IoPeople } from 'react-icons/io5'
import { request } from '../../../share/request'

const CreateGroupModal = ({ 
  open, 
  onClose, 
  allUsers = [], 
  currentUser,
  onGroupCreated,
  linkPath 
}) => {
  const [groupName, setGroupName] = useState('')
  const [groupAvatar, setGroupAvatar] = useState(null)
  const [groupAvatarFile, setGroupAvatarFile] = useState(null)
  const [selectedMembers, setSelectedMembers] = useState([])
  const [memberSearch, setMemberSearch] = useState('')
  const [creating, setCreating] = useState(false)

  // Filter users (exclude current user)
  const availableUsers = (allUsers || [])
    .filter(u => u && String(u.id) !== String(currentUser?.id))

  // Filter members based on search
  const filteredMembers = availableUsers.filter(u => {
    if (!memberSearch) return true
    const searchLower = memberSearch.toLowerCase()
    const fields = [
      u.fullname, 
      u.username, 
      u.staffname_eng, 
      u.frontname,
      u.position, 
      u.department_name
    ]
    return fields.some(f => f && String(f).toLowerCase().includes(searchLower))
  })

  // Reset form
  const resetForm = () => {
    setGroupName('')
    setGroupAvatar(null)
    setGroupAvatarFile(null)
    setSelectedMembers([])
    setMemberSearch('')
    setCreating(false)
  }

  // Handle close
  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Handle avatar upload
  const handleAvatarUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      setGroupAvatar(e.target.result)
      setGroupAvatarFile(file)
    }
    reader.readAsDataURL(file)
    return false // Prevent auto upload
  }

  // Remove avatar
  const removeAvatar = () => {
    setGroupAvatar(null)
    setGroupAvatarFile(null)
  }

  // Toggle member selection
  const toggleMember = (userId) => {
    setSelectedMembers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId)
      } else {
        return [...prev, userId]
      }
    })
  }

  // Select/Deselect all
  const toggleAllMembers = () => {
    if (selectedMembers.length === filteredMembers.length && filteredMembers.length > 0) {
      setSelectedMembers([])
    } else {
      setSelectedMembers(filteredMembers.map(u => u.id))
    }
  }

  // Create group using request function with FormData
  const handleCreateGroup = async () => {
    // Validate
    if (!groupName.trim()) {
      message.error('Please enter a group name')
      return
    }

    if (selectedMembers.length === 0) {
      message.error('Please select at least one member')
      return
    }

    setCreating(true)

    try {
      const formData = new FormData()
      formData.append('name', groupName.trim())
      formData.append('member_ids', JSON.stringify(selectedMembers))
      
      if (groupAvatarFile) {
        formData.append('avatar', groupAvatarFile)
      }

      // Debug: Log FormData contents
      console.log('=== FormData Contents ===')
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`${key}: File - ${value.name} (${value.size} bytes, ${value.type})`)
        } else {
          console.log(`${key}: ${value}`)
        }
      }

      // Using request function with FormData
      const data = await request('groupschat', 'POST', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('Response data:', data)

      if (data && data.success) {
        message.success('Group created successfully!')
        
        // Callback with group data
        if (onGroupCreated && data.group) {
          onGroupCreated({
            ...data.group,
            is_group: true,
            members: selectedMembers,
            member_count: selectedMembers.length + 1
          })
        }
        
        handleClose()
      } else {
        message.error(data?.error || 'Failed to create group')
      }
    } catch (error) {
      console.error('Error creating group:', error)
      message.error(error?.message || 'An error occurred while creating the group')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      title={
        <div style={{ fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IoPeople size={24} color="#52c41a" />
          Create New Group
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
      bodyStyle={{ padding: '20px 24px' }}
    >
      <div>
        {/* Group Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ position: 'relative' }}>
            {groupAvatar ? (
              <div style={{ position: 'relative' }}>
                <img 
                  src={groupAvatar} 
                  alt="Group Avatar" 
                  style={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '2px solid #e8e8e8'
                  }} 
                />
                <button
                  onClick={removeAvatar}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    background: '#ff4d4f',
                    border: 'none',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  <IoClose size={16} />
                </button>
              </div>
            ) : (
              <Upload
                accept="image/*"
                beforeUpload={handleAvatarUpload}
                showUploadList={false}
                maxCount={1}
              >
                <div style={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: '2px dashed #d9d9d9',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#fafafa',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#52c41a'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                >
                  <IoImage size={32} color="#bfbfbf" />
                  <span style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4 }}>Upload Avatar</span>
                </div>
              </Upload>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
            {groupAvatar ? 'Click X to remove avatar' : 'Click to upload group avatar'}
          </div>
        </div>

        {/* Group Name */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Group Name <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Input
            placeholder="Enter group name..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            size="large"
            maxLength={50}
            style={{ borderRadius: 8 }}
            onPressEnter={handleCreateGroup}
          />
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            {groupName.length}/50 characters
          </div>
        </div>

        {/* Member Selection */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontWeight: 500 }}>
              Select Members <span style={{ color: '#ff4d4f' }}>*</span>
              <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>
                ({selectedMembers.length} selected)
              </span>
            </label>
            {filteredMembers.length > 0 && (
              <Button 
                type="link" 
                size="small"
                onClick={toggleAllMembers}
                style={{ padding: 0 }}
              >
                {selectedMembers.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
          
          <Input
            placeholder="Search members..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            size="middle"
            style={{ borderRadius: 8, marginBottom: 8 }}
            prefix={<span style={{ color: '#bfbfbf' }}>🔍</span>}
          />
        </div>

        {/* Member List */}
        <div style={{ 
          border: '1px solid #e8e8e8', 
          borderRadius: 8, 
          maxHeight: 300, 
          overflowY: 'auto',
          padding: '4px 0',
          background: '#fafafa'
        }}>
          {filteredMembers && filteredMembers.length > 0 ? (
            filteredMembers.map(u => {
              const isSelected = selectedMembers.includes(u.id)
              const userAvatar = u.avatar || u.image
              
              return (
                <div
                  key={u.id}
                  onClick={() => toggleMember(u.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: isSelected ? '#f6ffed' : 'transparent',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = '#f0f0f0'
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'transparent'
                  }}
                >
                  <Checkbox 
                    checked={isSelected}
                    onChange={() => toggleMember(u.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  <div style={{ 
                    width: 36, 
                    height: 36, 
                    borderRadius: '50%', 
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: '#e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {userAvatar ? (
                      <img 
                        src={userAvatar.startsWith('http') ? userAvatar : `${linkPath}/${userAvatar.replace(/^\//, '')}`} 
                        alt={u.fullname || u.username} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    ) : (
                      <span style={{ fontWeight: 600, color: '#666', fontSize: 14 }}>
                        {((u.fullname || u.username) || 'U').slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {u.fullname || u.username}
                    </div>
                    <div style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {u.position || u.department_name || `ID ${u.id}`}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div style={{ color: '#52c41a', fontSize: 18, fontWeight: 'bold' }}>✓</div>
                  )}
                </div>
              )
            })
          ) : (
            <div style={{ padding: '30px 20px', textAlign: 'center', color: '#999' }}>
              {memberSearch ? (
                <>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                  <div>No members found for "{memberSearch}"</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>👥</div>
                  <div>No members available</div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ 
          marginTop: 20, 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 10,
          borderTop: '1px solid #f0f0f0',
          paddingTop: 16
        }}>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<IoPersonAdd size={18} />}
            onClick={handleCreateGroup}
            loading={creating}
            style={{ 
              background: '#52c41a', 
              borderColor: '#52c41a',
              borderRadius: 8
            }}
            disabled={!groupName.trim() || selectedMembers.length === 0}
          >
            Create Group
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CreateGroupModal